// "Ask Orca about this": builds the visible context (chip label + question)
// and a mock answer from the item itself, in the UI language. The real API
// will receive the same context and answer on the server.
import type { TimeFmt } from '@/hooks/useTime';
import i18n from '@/i18n';
import { nf, pct, usd } from '@/lib/format';
import { sinceLabel } from '@/lib/time';
import { money } from '@/features/alerts/format';
import type { Answer, AssetDay, Coin, Digest, Idea, NewsItem, PositionRow, PriceAlert, TimelineEvent, Upcoming, Video, WeekEvent, WhaleTx, XPost } from '@/types';

export type AskCtx = {
  kind: 'news' | 'event' | 'chart' | 'alert' | 'whale' | 'position' | 'post' | 'video' | 'digest' | 'ws' | 'coin' | 'page';
  /** line icon */
  icon: string;
  /** shown in the context chip: «BTC · Whale transfer» */
  label: string;
  /** short tag for the chat history */
  tag: string;
  question: string;
  answer: Answer;
  /** questions the Orca bar rotates while this object is on screen */
  suggestions: string[];
  /** where the item lives (for «Based on») */
  to?: string;
  /** a locked feature's preview: Orca explains the feature, it has no data of it */
  lockedFeature?: string;
};
/** t of the ai:ctx namespace (keys built at runtime) */
const t = (key: string, o?: Record<string, unknown>) => (i18n.t as unknown as (k: string, o?: object) => string)(`ai:ctx.${key}`, o);
const src = (l: string, to?: string): Answer['sources'] => (to ? [[l, to]] : undefined);
const risk = () => t('risk');

export const newsCtx = (n: NewsItem, tf: TimeFmt): AskCtx => ({
  kind: 'news', icon: 'news', label: t('news.label', { tags: n.tags.slice(0, 2).join(', '), time: tf.time(n.at) }), tag: n.tags[0] ?? i18n.t('nav:page.news'),
  question: t('news.q', { title: n.title }), to: `/news?id=${n.id}`,
  suggestions: [t('news.s1'), t('news.s2', { what: n.tags[0] ?? t('news.market') }), t('news.s3'), t('news.s4')],
  answer: { lead: n.dek, known: n.body || n.dek, scenario: n.why ?? '', risk: risk(), sources: src(t('news.src', { when: tf.when(n.at) }), `/news?id=${n.id}`) },
});

export const eventCtx = (e: WeekEvent, tf: TimeFmt): AskCtx => ({
  kind: 'event', icon: 'cal', label: `${e.short} · ${tf.dayShort(e.at)}, ${tf.dateLong(e.at)} ${tf.time(e.at)} ${tf.zone}`, tag: i18n.t('nav:page.calendar'),
  question: t('event.q', { title: e.title, assets: e.assets.slice(0, 3).join(', ') }), to: `/calendar?e=${e.id}`,
  suggestions: /FOMC|ФРС|Fed/i.test(e.title + e.short)
    ? [t('event.fed1'), t('event.fed2'), t('event.fed3')]
    : [t('event.s1', { what: e.short }), t('event.s2'), t('event.s3', { what: e.short, asset: e.assets[0] ?? t('news.market') })],
  answer: {
    lead: t(`event.lead${e.imp}`, { title: e.title, assets: e.assets.slice(0, 3).join(', ') }),
    known: `${e.why}${e.prev ? ` ${e.fc ? t('event.prevFc', { prev: e.prev, fc: e.fc }) : t('event.prev', { prev: e.prev })}` : ''}`,
    scenario: e.fc ? t('event.scenFc', { fc: e.fc }) : t('event.scen'),
    risk: t('event.risk'),
    sources: src(t('event.src', { what: e.short }), `/calendar?e=${e.id}`),
  },
});

export const chartCtx = (coin: Coin, e: TimelineEvent, tf: TimeFmt): AskCtx => ({
  kind: 'chart', icon: e.type, label: `${coin.symbol} · ${tf.time(e.at)} · ${e.title.slice(0, 32)}${e.title.length > 32 ? '…' : ''}`, tag: coin.symbol,
  question: t('chart.q', { title: e.title, coin: coin.symbol }), to: `/signals?coin=${coin.symbol}`,
  suggestions: [t('chart.s1', { coin: coin.symbol }), t('chart.s2'), t('chart.s3')],
  answer: {
    lead: e.move !== undefined ? t('chart.leadMove', { time: tf.time(e.at), coin: coin.symbol, ch: pct(e.move, 1) }) : t('chart.lead', { time: tf.time(e.at), coin: coin.symbol }),
    known: `${e.title}. ${e.body}.${e.note ? ` ${e.note}.` : ''}`,
    scenario: e.move !== undefined && Math.abs(e.move) >= 1 ? t('chart.scenBig') : t('chart.scenSmall'),
    risk: t('chart.risk'),
    sources: src(t('chart.src', { coin: coin.symbol }), `/signals?coin=${coin.symbol}`),
  },
});

export const upcomingCtx = (coin: Coin, u: Upcoming, tf: TimeFmt): AskCtx => ({
  kind: 'event', icon: 'cal', label: `${u.title} · ${tf.when(u.at)}`, tag: coin.symbol,
  question: t('upcoming.q', { title: u.title, coin: coin.symbol }), to: u.calendarId ? `/calendar?e=${u.calendarId}` : undefined,
  suggestions: [t('upcoming.q', { title: u.title, coin: coin.symbol }), t('upcoming.s2'), t('upcoming.s3')],
  answer: { lead: t(u.imp === 3 ? 'upcoming.leadTop' : 'upcoming.lead', { title: u.title, when: tf.when(u.at), coin: coin.symbol }), known: u.why, scenario: t('upcoming.scen'), risk: risk() },
});

export const alertCtx = (a: PriceAlert, tf: TimeFmt): AskCtx => ({
  kind: 'alert', icon: 'price', label: t('alert.label', { coin: a.symbol, ch: pct(a.change, 2), time: tf.time(a.at) }), tag: a.symbol,
  question: t(a.change < 0 ? 'alert.qDown' : 'alert.qUp', { coin: a.symbol, ch: pct(Math.abs(a.change), 1) }), to: `/alerts?coin=${a.symbol}`,
  suggestions: [t('alert.s1', { coin: a.symbol }), t('alert.s2', { coin: a.symbol })],
  answer: {
    lead: t(a.change < 0 ? 'alert.leadDown' : 'alert.leadUp', { coin: a.symbol, ch: pct(Math.abs(a.change), 2), level: `${a.level > 0 ? '+' : '−'}${Math.abs(a.level)}%` }),
    known: t('alert.known', { price: usd(a.price, 2), coin: a.symbol }),
    scenario: a.change < 0 ? t('alert.scenDown') : t('alert.scenUp'),
    risk: `${risk()}${a.position ? ` ${t('alert.posIncluded', { qty: nf(a.position.qty, 2), coin: a.symbol })}` : ''}`,
    sources: src(t('alert.src', { when: tf.when(a.at) }), `/alerts?coin=${a.symbol}`),
  },
});

export const whaleCtx = (w: WhaleTx, network: string, flow: string): AskCtx => ({
  kind: 'whale', icon: 'whale', label: t('whale.label', { token: w.token, net: network }), tag: w.token,
  question: t('whale.q', { amount: nf(w.amount), token: w.token, flow: flow.toLowerCase() }), to: '/whales',
  suggestions: [t('whale.s1', { token: w.token }), t('whale.s2'), t('whale.s3')],
  answer: {
    lead: t('whale.lead', { usd: money(w.usd), from: w.from.label, to: w.to.label }),
    known: t('whale.known', { net: network, amount: nf(w.amount), token: w.token, flow: flow.toLowerCase() }),
    scenario: w.flow === 'inflow' ? t('whale.scenIn') : w.flow === 'outflow' ? t('whale.scenOut') : t('whale.scenOther'),
    risk: t('whale.risk'),
    sources: src('OrcaWatch', '/whales'),
  },
});

export const positionCtx = (r: PositionRow): AskCtx => ({
  kind: 'position', icon: 'pie', label: t('position.label', { coin: r.symbol, qty: nf(r.qty, 4) }), tag: r.symbol,
  question: t('position.q', { coin: r.symbol }), to: '/portfolio',
  suggestions: [t('position.s1', { coin: r.symbol }), t('position.s2', { coin: r.symbol }), t('position.s3', { coin: r.symbol })],
  answer: {
    lead: r.incomplete
      ? t('position.leadIncomplete', { coin: r.symbol, share: nf(r.share, 1) })
      : t(r.estimated ? 'position.leadEst' : 'position.lead', { coin: r.symbol, pnl: pct(r.pnlPct, 1), share: nf(r.share, 1) }),
    known: `${t('position.known', { qty: nf(r.qty, 4), coin: r.symbol, avg: usd(r.avgPrice, r.decimals) })}${r.since ? `, ${i18n.t('portfolio:pos.inPosition', { since: sinceLabel(r.since, r.sinceSource) })}` : ''}; ${t('position.now', { price: usd(r.price, r.decimals), ch: pct(r.change24h, 1) })}`,
    scenario: r.share > 35 ? t('position.scenBig') : t('position.scenSmall'),
    risk: t('position.risk'),
    sources: src(i18n.t('nav:page.portfolio'), '/portfolio'),
  },
});

/** A question about «my position» in a coin the user holds no position in:
 *  Orca says so honestly and offers to add it (no invented numbers). */
export const noPositionAnswer = (symbol: string, watched: boolean): Answer => ({
  lead: t('noPos.lead', { coin: symbol }),
  known: watched ? t('noPos.watched', { coin: symbol }) : t('noPos.notWatched', { coin: symbol }),
  scenario: t('noPos.scen'),
  risk: t('noPos.risk'),
  action: { kind: 'add-position', symbol, label: t('noPos.action') },
  sources: src(i18n.t('nav:page.portfolio'), '/portfolio'),
});

export const postCtx = (p: XPost, tf: TimeFmt): AskCtx => ({
  kind: 'post', icon: 'x', label: `X · @${p.handle} · ${tf.time(p.at)}`, tag: p.symbol,
  question: t('post.q', { handle: p.handle, coin: p.symbol }), to: `/social?coin=${p.symbol}`,
  suggestions: [t('post.s1', { coin: p.symbol }), t('post.s2')],
  answer: { lead: t(`post.lead${p.relevance ?? 2}`, { who: p.who, coin: p.symbol }), known: p.text, scenario: p.why ?? '', risk: t('post.risk'), sources: src(`X Radar · ${tf.when(p.at)}`, `/social?coin=${p.symbol}`) },
});

export const videoCtx = (v: Video): AskCtx => ({
  kind: 'video', icon: 'yt', label: v.channel ? `YouTube · ${v.channel}` : 'YouTube', tag: v.symbols[0] ?? 'YouTube',
  question: t('video.q', { title: v.title }), to: '/social',
  suggestions: [t('video.s1'), t('video.s2', { what: v.symbols[0] ?? t('news.market') }), t('video.s3')],
  answer: { lead: v.channel ? t('video.lead', { channel: v.channel, duration: v.duration ?? '' }) : v.title, known: (v.theses ?? [v.title]).join(' '), scenario: t('video.scen', { coins: v.symbols.join(', ') }), risk: t('video.risk'), sources: src('YouTube Analytics', '/social') },
});

export const digestCtx = (d: Digest, pre: boolean, tf: TimeFmt, nextAt?: string): AskCtx => ({
  kind: 'digest', icon: 'ws', label: `Wall Street · ${pre ? 'Pre-Market' : 'Post-Market'} · ${tf.when(d.at)}`, tag: 'Wall Street',
  question: pre ? t('digest.qPre') : t('digest.qPost'), to: '/wall-street',
  suggestions: [t('digest.s1'), pre ? t('digest.s2Pre') : t('digest.s2Post'), t('digest.s3')],
  answer: { lead: d.lede, known: d.themes.join(' '), scenario: nextAt ? t('digest.next', { when: tf.when(nextAt), until: tf.until(nextAt) }) : t('digest.nextDay'), risk: t('digest.risk'), sources: src('Wall Street', '/wall-street') },
});

/** A single line of the digest: theme, mover, earnings, macro or index. */
export const wsItemCtx = (label: string, question: string, known: string): AskCtx => ({
  kind: 'ws', icon: 'ws', label: `Wall Street · ${label}`, tag: 'Wall Street', question, to: '/wall-street',
  suggestions: [question, t('digest.s1')],
  answer: { known, scenario: t('wsItem.scen'), risk: t('wsItem.risk'), sources: src('Wall Street', '/wall-street') },
});

export const coinCtx = (c: Coin): AskCtx => ({
  kind: 'coin', icon: 'link', label: `${c.symbol} · ${c.name}`, tag: c.symbol,
  question: i18n.t('ai:ctx.coin.now', { coin: c.symbol }), to: `/signals?coin=${c.symbol}`,
  suggestions: [i18n.t('ai:ctx.coin.now', { coin: c.symbol }), t(c.change24h < 0 ? 'coin.weaker' : 'coin.stronger', { coin: c.symbol }), t('coin.week', { coin: c.symbol })],
  answer: {
    lead: t('coin.lead', { coin: c.symbol, price: usd(c.price, c.decimals), ch: pct(c.change24h, 1) }),
    known: t('coin.known', { coin: c.symbol }),
    scenario: c.change24h < 0 ? t('coin.scenWeak') : t('coin.scenStrong'),
    risk: risk(), sources: src(t('chart.src', { coin: c.symbol }), `/signals?coin=${c.symbol}`),
  },
});

/** Signals: the coin with its 7-day forecast. */
export const forecastCtx = (c: Coin, day: AssetDay): AskCtx => ({
  ...coinCtx(c),
  kind: 'coin', label: t('forecast.label', { coin: c.symbol }),
  answer: {
    lead: day.forecastText, known: day.forecastFull,
    // the real forecast is a text: no made-up corridor goes into the question
    scenario: day.forecast ? t('forecast.scen', { bear: `${day.forecast.bear[0]}…${day.forecast.bear[1]}%`, base: `${day.forecast.base[0]}…${day.forecast.base[1]}%`, bull: `${day.forecast.bull[0]}…${day.forecast.bull[1]}%` }) : day.forecastFull,
    risk: t('forecast.risk'), sources: [[t('chart.src', { coin: c.symbol }), `/signals?coin=${c.symbol}`]],
  },
  suggestions: [t('forecast.s1', { coin: c.symbol }), t('forecast.s2', { coin: c.symbol }), t('coin.week', { coin: c.symbol }), i18n.t('ai:ctx.coin.now', { coin: c.symbol })],
});

/** «ORCA IDEAS · Ideas of the day» item. */
export const ideaCtx = (idea: Idea): AskCtx => ({
  kind: 'news', icon: 'ai', label: t('idea.label', { tags: idea.tags.join(', ') }), tag: idea.tags[0] ?? 'ORCA IDEAS',
  question: t('idea.q', { title: idea.title }), to: '/#ideas',
  answer: { lead: idea.dek, known: t('idea.known', { title: idea.title, source: idea.source.name }), scenario: t('idea.scen'), risk: t('idea.risk'), sources: [[i18n.t('overview:ideas.title'), '/#ideas']] },
  suggestions: [i18n.t('overview:ideas.check'), t('idea.s2'), t('idea.s3')],
});
